/* ═════════════════════════ In-App Browser (web equivalent) ═════════════════════════
   Full-screen modal containing an <iframe> of the affiliate destination + a
   clean "Back to Atelier" header.  Keeps the user inside the app experience
   instead of jumping out to the system browser.

   When the app is wrapped with Capacitor on mobile, swap the <iframe> for
   `@capacitor/browser` (Browser.open({ url })) which fires a native in-app
   browser. The component signature stays identical.
   ─────────────────────────────────────────────────────────────────────────── */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ExternalLink, Loader2 } from 'lucide-react';

const GOLD    = '#C5A059';
const GOLD_HI = '#FFE9A0';

export const InAppBrowser = ({ url, title = 'External Link', onClose }) => {
  const [loading, setLoading] = useState(true);

  return (
    <AnimatePresence>
      {url && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[400] flex flex-col"
          style={{ background: '#050308' }}
          data-testid="in-app-browser">
          {/* Header — clear Back button */}
          <div className="flex items-center justify-between px-3 py-3 border-b"
            style={{ borderColor: 'rgba(197,160,89,0.25)' }}>
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{
                background: 'rgba(197,160,89,0.08)',
                border: `1px solid ${GOLD}66`,
                color: GOLD,
              }}
              data-testid="in-app-browser-back">
              <ChevronLeft size={16}/>
              <span className="font-['Outfit'] text-[11px] uppercase tracking-[0.2em]">
                Back
              </span>
            </button>
            <span
              className="font-['Cinzel'] tracking-[0.2em] truncate max-w-[55vw]"
              style={{ color: GOLD_HI, fontSize: 12 }}
              data-testid="in-app-browser-title">
              {title}
            </span>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-8 h-8 rounded-full"
              style={{ background: 'rgba(197,160,89,0.08)', color: GOLD, border: `1px solid ${GOLD}44` }}
              aria-label="Open in new tab"
              data-testid="in-app-browser-external">
              <ExternalLink size={14}/>
            </a>
          </div>

          {/* Loading shimmer until the iframe finishes */}
          {loading && (
            <div className="absolute inset-x-0 flex items-center justify-center pointer-events-none"
              style={{ top: 56, bottom: 0 }}>
              <div className="flex flex-col items-center gap-3">
                <Loader2 size={28} className="animate-spin" style={{ color: GOLD }}/>
                <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em]"
                  style={{ color: 'rgba(197,160,89,0.5)' }}>
                  Loading destination
                </span>
              </div>
            </div>
          )}

          {/* The actual content — iframe (web) / native browser (Capacitor) */}
          <iframe
            src={url}
            title={title}
            className="flex-1 w-full"
            style={{ border: 'none', background: '#fff' }}
            onLoad={() => setLoading(false)}
            referrerPolicy="no-referrer-when-downgrade"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            data-testid="in-app-browser-iframe"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InAppBrowser;
